<template>
  <div v-if="progress" class="tp" :class="['tp--' + variant, { 'tp--dark': dark }]">
    <template v-if="variant === 'full'">
      <div class="tp-head">
        <span class="tp-label">
          เป้า {{ formatNumber(progress.displayedTarget) }} ฿<template v-if="note"> · {{ note }}</template>
        </span>
        <span class="tp-pct" :class="'tp-pct--' + progress.color">{{ pctText }}</span>
      </div>
      <div class="tp-track">
        <div class="tp-fill" :class="'tp-fill--' + progress.color" :style="{ width: fillWidth + '%' }"></div>
      </div>
    </template>
    <span v-else class="tp-pill" :class="'tp-pill--' + progress.color" :title="targetTitle">
      <i class="fas fa-bullseye tp-pill-ico"></i>เป้า {{ pctText }}
    </span>
  </div>
</template>

<script>
import kpiTargetProgress from "@/utils/kpiTargetProgress";
import { formatNumber } from "@/utils/utils";

export default {
  name: "TargetProgress",
  props: {
    revenueTarget: { type: Object, default: null }, // { periodType, periodKey, fullTarget } | null
    actual: { type: Number, default: 0 },
    startDt: { type: String, default: "" },
    endDt: { type: String, default: "" },
    variant: { type: String, default: "full" }, // "full" | "badge"
    dark: { type: Boolean, default: false }, // on-dark palette (branch hero feature card)
    // ข้อความกำกับต่อท้ายป้ายเป้า เช่น "จาก 2/3 สาขา" ใช้เมื่อเป้าที่แสดงไม่ได้มาจาก
    // ทุกหน่วยที่นับรวมอยู่ในตัวเลข actual — เว้นว่างเมื่อไม่มีอะไรต้องเตือน
    note: { type: String, default: "" },
  },
  computed: {
    progress() {
      return kpiTargetProgress({
        revenueTarget: this.revenueTarget,
        actual: this.actual,
        startDt: this.startDt,
        endDt: this.endDt,
      });
    },
    pctText() {
      return this.progress && this.progress.attainmentPct != null
        ? this.progress.attainmentPct + "%"
        : "—";
    },
    fillWidth() {
      // Cap the fill at 100% so the bar never overflows past the track.
      if (!this.progress || this.progress.attainmentPct == null) return 0;
      return Math.min(100, Math.max(0, this.progress.attainmentPct));
    },
    targetTitle() {
      return this.progress ? "เป้า " + formatNumber(this.progress.displayedTarget) + " ฿" : "";
    },
  },
  methods: {
    formatNumber(val) {
      return formatNumber(val || 0);
    },
  },
};
</script>

<style scoped>
/* Cohesive with ClinicDashboard/BranchDashboard: .treat-fill bar + .pill badge. */
.tp { margin-top: 0; }
.tp-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.tp-label { font-size: 11px; font-weight: 600; color: #6b7280; }
.tp-pct { font-size: 12.5px; font-weight: 700; font-variant-numeric: tabular-nums; }
.tp-pct--green { color: #0f9d72; }
.tp-pct--amber { color: #f59e0b; }
.tp-pct--neutral { color: #6b7280; }

/* Bar — mirrors .treat-track / .treat-fill */
.tp-track { height: 7px; border-radius: 999px; background: #f5f8fb; overflow: hidden; }
.tp-fill { height: 100%; border-radius: 999px; transition: width 0.4s ease; }
.tp-fill--green { background: linear-gradient(90deg, #0f9d72, #3ec79a); }
.tp-fill--amber { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
.tp-fill--neutral { background: #cbd5e1; }

/* Badge variant — mirrors .pill / .pill--ok / .pill--warn */
.tp-pill {
  display: inline-block;
  white-space: nowrap;
  font-weight: 600;
  font-size: 12px;
  padding: 3px 9px;
  border-radius: 7px;
  font-variant-numeric: tabular-nums;
}
.tp-pill-ico { margin-right: 4px; font-size: 9px; opacity: 0.85; }
.tp-pill--green { background: #e6f6f0; color: #0f9d72; }
.tp-pill--amber { background: #fdf3e2; color: #f59e0b; }
.tp-pill--neutral { background: #eef1f6; color: #6b7280; }

/* On-dark surface — mirrors .hero-badge / .hero-badge-v on .hero-card--feat */
.tp--dark .tp-label { color: #dbe6ff; opacity: 0.9; }
.tp--dark .tp-track { background: rgba(255, 255, 255, 0.18); }
.tp--dark .tp-pct--green { color: #7ff0c4; }
.tp--dark .tp-pct--amber { color: #ffd28a; }
.tp--dark .tp-pct--neutral { color: #eaf1ff; }
.tp--dark .tp-fill--green { background: linear-gradient(90deg, #3ec79a, #7ff0c4); }
.tp--dark .tp-fill--amber { background: linear-gradient(90deg, #f59e0b, #ffd28a); }
</style>
